import { useMemo, useState } from 'react';

import { useForm, useWatch } from 'react-hook-form';
import { WidgetViewMode, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { UseBaseTable } from 'hooks/useBaseTable';
import useCustomExcelFileName from 'hooks/useCustomExcelFileName';
import { useGlobalFiltersHook } from 'hooks/useGlobalFiltersHook';

import { useMutationExportCsvReport } from 'api/mutations/exportCsvReport.mutation';
import { useQueryFinishDateChangesTableData } from 'api/queries/finishDateChangesTableData.query';

import { FilterNames, getFiltersData } from 'components/common/GlobalFiltersInPage/globalFiltersInPage.utils';

import { IHashmap } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { TKeys } from 'components/Dashboards/Project/Components/VersionCompare/utils/helper';
import { ITableLocalFiltersFields } from 'components/widgets/ChangeAnalysisTrackedActivities/tableLocalFilters/tableLocalFilters';
import { statusOptions } from 'components/widgets/KeyActivities/components/filter.utils';

export type UseFinishDateChangesProvider = ReturnType<typeof useFinishDateChangesProvider>;

interface Props {
    componentKey: keyof IHashmap;
    widgetId?: string;
    globalFilterData: any;
    localFilters: any;
    compareVersionId: number | undefined;
    latestVersionId: number | undefined;
    title: string;
    projectId: number | null | undefined;
    contractId: number | null | undefined;
    table: UseBaseTable['table'];
    widgetViewMode: WidgetViewMode;
    endpoints: Record<string, string>;
}

export const useFinishDateChangesProvider = ({
    widgetId,
    globalFilterData,
    localFilters,
    compareVersionId,
    latestVersionId,
    title,
    projectId,
    contractId,
    widgetViewMode,
    endpoints,
}: Props) => {
    const isInPinboard = Boolean(widgetId);
    const [activeTab, setActiveTab] = useState<TKeys>(
        WidgetViewModeUtils.isReportEditWidget(widgetViewMode) ||
            WidgetViewModeUtils.isReportOrPinboardView(widgetViewMode)
            ? (localFilters?.activeTab as TKeys)
            : 'next',
    );
    const form = useForm<ITableLocalFiltersFields>({
        defaultValues: {
            status: statusOptions,
            criticalPath: false,
        },
    });
    const formValues = useWatch({ control: form.control });
    const source = isInPinboard
        ? { ...localFilters, activeTab }
        : {
              ...globalFilterData,
              statusFilter: formValues?.status,
              isCriticalPath: formValues.criticalPath,
              activeTab,
          };

    const filters = useMemo(() => {
        return getFiltersData({
            [FilterNames.ACTIVITY_CODES]: source?.activityCodes?.filter((item) => !item.isExclude).map((i) => i.name),
            [FilterNames.ACTIVITY_CODES_EXCLUDE]: source?.activityCodes
                ?.filter((item) => item.isExclude)
                .map((i) => i.name),
            [FilterNames.FRAGNET_SEARCH]: source?.milestoneFragnet,
            [FilterNames.WBS_SEARCH]: source?.wbs,
            [FilterNames.TAG_CARD_BOARD]: source?.tags?.filter((item) => !item.isExclude).map((i) => i.name),
            [FilterNames.TAG_CARD_BOARD_EXCLUDE]: source?.tags?.filter((item) => item.isExclude).map((i) => i.name),
        });
    }, [source?.activityCodes, source?.milestoneFragnet, source.wbs, source?.tags]);

    // create pill data for PinboardWidgetPills component
    const pillsData = {
        activityCodes: source?.activityCodes?.filter((item) => !item.isExclude).map((i) => i.name),
        activityCodesExclude: source?.activityCodes?.filter((item) => item.isExclude).map((i) => i.name),
        milestoneFragnet: source?.milestoneFragnet,
        tags: source?.tags?.filter((item) => !item.isExclude).map((i) => i.name),
        tagsExclude: source?.tags?.filter((item) => item.isExclude).map((i) => i.name),
        wbs: source?.wbs,
        statusFilter: source?.statusFilter?.map((item) => item.label),
        isCriticalPath: source?.isCriticalPath,
    };

    const { pillsList: pills } = useGlobalFiltersHook({ source: pillsData, latestVersionId, widgetId });

    const getFinishDateChangesTableDataPayload = {
        projectId: projectId,
        contractId: contractId,
        latestVersionId: latestVersionId,
        compareVersionId: compareVersionId,
        filter: {
            filters,
        },
    };

    const getFinishDateChangesTableData = useQueryFinishDateChangesTableData(
        getFinishDateChangesTableDataPayload,
        endpoints.getTopFinishDateChanges,
    );

    const reportType = getFinishDateChangesTableData.data?.typeId || '';
    const fileName = useCustomExcelFileName({
        projectId,
        contractId,
        latestVersionId,
        title,
        prefix: 'Change_Analysis_',
    });

    const exportCsvReport = {
        instance: useMutationExportCsvReport(),
        payload: {
            compareVersionId,
            contractId,
            filter: { filters },
            latestVersionId,
            period: source.activeTab,
            projectId,
            reportType,
            fileName,
        },
    };

    return {
        ui: {
            state: {
                statusFilter: source.statusFilter,
                isCriticalPath: source.isCriticalPath,
                activityCodes: source.activityCodes,
                milestoneFragnet: source.milestoneFragnet,
                tags: source.tags,
                wbs: source.wbs,
                activeTab: source.activeTab,
            },
            setters: {
                setActiveTab,
            },
            isInPinboard,
            pills,
            form,
        },
        api: {
            getFinishDateChangesTableData: {
                instance: getFinishDateChangesTableData,
                payload: getFinishDateChangesTableDataPayload,
                endpoint: endpoints.getTopFinishDateChanges,
            },
            exportCsvReport,
        },
    };
};
